import { useAuthFetch } from "@/hooks/data/useAuthFetch";
import { leadTypeQueryKeys } from "@/hooks/data/v2/query-keys";
import { parseResponse } from "@/lib/query-helper";
import { APIResponse, PaginatedResponse, SearchFilter } from "@simplerealtybits/srb-types";
import { LeadType } from "@t/LeadType";
import { LeadTypeViewerDto } from "@t/LeadTypeViewerDto";
import { QueryClient, UndefinedInitialDataOptions, useQuery } from "@tanstack/react-query";
import { useCallback } from "react";

export const useGetLeadTypesByFilter = (filter: SearchFilter | null) => {
    const authFetch = useAuthFetch();

    const getLeadTypesByFilter = async (): Promise<PaginatedResponse<LeadType>> => {
        return await authFetch("/api/v1/lead-types/get-by-filter", {
            method: "POST",
            body: JSON.stringify(filter),
        }).then((response) => response.json());
    };

    const getLeadTypesByFilterQueryOptions = () => ({
        queryKey: leadTypeQueryKeys.getByFilter(filter),
        queryFn: () => getLeadTypesByFilter(),
        enabled: !!filter,
        select: (data: PaginatedResponse<LeadType>) => data.data,
    });

    return useQuery(getLeadTypesByFilterQueryOptions());
};

type UseGetLeadTypeForViewerOptions = UndefinedInitialDataOptions<
    APIResponse<LeadTypeViewerDto>,
    Error,
    LeadTypeViewerDto,
    readonly unknown[]
>;

export const useGetLeadTypeForViewer = (
    leadTypeId: number,
    options?: UseGetLeadTypeForViewerOptions,
    queryClient?: QueryClient
) => {
    const authFetch = useAuthFetch();

    const queryFn = useCallback(async (): Promise<APIResponse<LeadTypeViewerDto>> => {
        const response = await authFetch(`/api/v1/lead-types/${leadTypeId}/viewer`);
        return await parseResponse(response);
    }, [authFetch, leadTypeId]);

    const getLeadTypeForViewerOptions = (leadTypeId?: number): UseGetLeadTypeForViewerOptions => ({
        queryKey: leadTypeQueryKeys.getById(leadTypeId ?? 0),
        queryFn: queryFn,
        enabled: !!leadTypeId,
        select: (data: APIResponse<LeadTypeViewerDto>) => data.data,
    });

    return useQuery(
        {
            ...getLeadTypeForViewerOptions(leadTypeId),
            ...options,
        },
        queryClient
    );
};
